<template>
	<div :style="{ '--maxCountWidth': maxCountWidth }">
		<h6 class="is-visuallyHidden">{{ $t('sightingsList.timePeriodObservations', [checklists.length]) }}</h6>
		<div class="ReportListSpeciesObs ReportListSpeciesObs--header">
			<div class="ReportListSpeciesObs-count">
				<abbr :title="$t('data.individuals')">{{ $t('data.individuals.abbr') }}</abbr>
			</div>
			<div class="ReportListSpeciesObs-date">{{ $t('generic.Date') }}</div>
			<div class="ReportListSpeciesObs-location">{{ $t('generic.Location') }}</div>
			<div class="ReportListSpeciesObs-comments u-showForLarge">{{ $t('generic.Comments') }}</div>
		</div>
		<ol>
			<li v-for="checklist in checklists" :key="checklist.subId">
				<div class="ReportListSpeciesObs">
					<div class="ReportListSpeciesObs-count">
						<Count :count="checklist.howMany" :locale="locale" />
					</div>
					<div class="ReportListSpeciesObs-date">
						<Checklist :subId="checklist.subId" :date="checklist.obsDt" :locale="locale" :baseUrl="baseUrl" />
						<ExoticBadge v-if="checklist.exoticCategory" :exoticCategory="checklist.exoticCategory" size="xs"/>
					</div>
					<div class="ReportListSpeciesObs-location">{{ checklist.locName }}</div>
					<div v-if="checklist.obsComments" v-html="checklist.obsComments" class="ReportListSpeciesObs-comments"></div>
				</div>
			</li>
		</ol>
	</div>
</template>

<script>
import Count from './Count.vue';
import Checklist from './Checklist.vue';
import ExoticBadge from './ExoticBadge.vue';

export default {
	name: 'ReportList',
	components: {
		Count,
		Checklist,
		ExoticBadge,
	},
	props: {
		checklists: {
			type: Array,
			required: true,
		},
		locale: String,
	},
	data() {
		return {
			baseUrl: window.baseUrl,
		};
	},
	computed: {
		maxCountWidth: function () {
			// Calculate the width needed to display the maxium count number in the list
			const counts = this.checklists.map((obs) => obs.howMany);
			const maxCount = Math.max(...counts);
			const maxCountDigits = maxCount.toString().length;
			// default width of 1rem for small numbers
			let width = 1.5;
			// 3-digit and up...
			if (maxCountDigits >= 3) {
				if (maxCountDigits < 5) {
					// 3-4 digits
					width = maxCountDigits * 0.5;
				} else if (maxCountDigits < 7) {
					// 5-6 digits
					width = maxCountDigits * 0.6;
				} else {
					// 7 or more digits
					width = maxCountDigits * 0.7;
				}
			}
			return width + 'rem';
		},
	},
};
</script>

<style lang="scss">
.ReportListSpeciesObs {
	@include text(2);
	display: grid;
	grid-template-columns: auto 8.5rem 1fr 1fr;
	grid-template-rows: auto;
	column-gap: $space-sm;
	row-gap: $space-xs;
	@include breakpoint(medium) {
		column-gap: $space-md;
	}
}
.ReportListSpeciesObs-count {
	text-align: right;
	min-width: 2rem;
	min-width: var(--maxCountWidth, 1.5rem);
	grid-column: 1 / 2;
	grid-row: 1 / 2;
	.Count {
		min-width: auto;
	}
}
.ReportListSpeciesObs-date {
	grid-column: 2 / 3;
	grid-row: 1 / 2;
	display: flex;
	gap: $space-xs;
	align-content: center;
	justify-content: space-between;
}
.ReportListSpeciesObs-location {
	grid-column: 3 / -1;
	grid-row: 1 / 2;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	@include breakpoint(large) {
		grid-column: 3 / 4;
	}
}
.ReportListSpeciesObs-comments {
	color: $color-neutral-4;
	grid-column: 2 / -1;
	grid-row: 2 / 3;
	@include breakpoint(large) {
		grid-column: 4 / 5;
		grid-row: 1 / 2;
	}
}

.ReportListSpeciesObs--header {
	@include text(1);
	color: $color-neutral-3;
	text-transform: uppercase;
	border-bottom: 1px solid $color-neutral-1;
	padding-bottom: $space-xs;
	@include stack-sm;
	abbr[title] {
		border-bottom: none;
		text-decoration: none;
	}
}

.print {
	.ReportListSpeciesObs {
		page-break-inside: avoid;
		display: flex;
		flex-wrap: wrap;
		column-gap: $space-md;
		row-gap: $space-xs;
	}
	.Checklist {
		@include interactiveColor(color, $color-text, $color-text);
	}
	.ReportListSpeciesObs--header {
		border-style: none;
		padding-bottom: 0;
		@include stack-xs;
		page-break-after: avoid;
		.ReportListSpeciesObs-comments {
			display: none;
		}
	}
	.ReportListSpeciesObs-count {
		flex: 0 0 auto;
		width: var(--maxCountWidth, 1.5rem);
	}
	.ReportListSpeciesObs-date {
		flex: 0 0 auto;
		width: 8rem;
	}
	.ReportListSpeciesObs-location {
		// grid-column: 3 / -1;
		// grid-row: 1 / 2;
		flex: 1 0 auto;
		overflow: hidden;
		white-space: pre-wrap;
		text-overflow: none;
		@include breakpoint(large) {
			grid-column: 3 / -1;
		}
	}
	.ReportListSpeciesObs-comments {
		// @include text(3, loose);
		flex: 1 1 auto;
		width: 100%;
		padding-left: calc(var(--maxCountWidth, 1.5rem) + $space-md);
		color: $color-text;
		@include breakpoint(large) {
			grid-column: 2 / -1;
			grid-row: 2 / 3;
		}
	}
}
</style>
